/**
 * Combined OpenAPI Specification
 *
 * Aggregates OpenAPI specs from all domain contracts.
 * This provides unified API documentation for the entire application.
 */

import { analyticsOpenApiSpec } from './analytics/index.js';
import { jobsOpenApiSpec, workOrderDirectionalOpenApiSpec } from './jobs/index.js';
import { plannerOpenApiSpec } from './planner/index.js';
import { settingsOpenApiSpec } from './settings/index.js';
import { teamsOpenApiSpec } from './teams/index.js';
import { usersOpenApiSpec } from './users/index.js';

/**
 * Helper to prefix all paths in an OpenAPI spec with a base path
 */
function prefixPaths(paths: Record<string, any> | undefined, prefix: string): Record<string, any> {
  if (!paths) return {};

  const prefixedPaths: Record<string, any> = {};
  for (const [path, methods] of Object.entries(paths)) {
    prefixedPaths[`${prefix}${path}`] = methods;
  }
  return prefixedPaths;
}

/**
 * Combined OpenAPI specification for Skewb Works API
 *
 * Merges all domain-specific OpenAPI specs into a single unified documentation.
 * This provides a complete view of all API endpoints across the application.
 *
 * Currently includes:
 * - Analytics API (dashboard KPIs, jobs analytics, performance)
 * - Jobs API (job list, categories, priorities, detail, DFE, documents, data capture)
 * - Users API (user management, roles, verification)
 * - Teams API (CRUD, member management, utilization)
 * - Planner API (scheduling, events, unscheduled/scheduled job queues)
 * - Settings API (job categories, task types, category-task assignments)
 * - Work Orders API (directional communication endpoints)
 *
 * IMPORTANT: This API documentation is protected and only accessible
 * to authenticated users.
 */
export const openApiSpec = {
  openapi: analyticsOpenApiSpec.openapi,
  info: {
    title: 'Skewb Works API',
    version: '1.0.0',
    description:
      'Complete API documentation for Skewb Works application. ' +
      'All endpoints use ts-rest contracts with Zod validation and return typed responses. ' +
      '\n\n**Authentication:** All endpoints require valid session authentication.',
    contact: {
      name: 'Skewb Works Development Team',
    },
  },
  // Base servers without domain prefixes
  servers: [
    {
      url: 'https://localhost:8080',
      description: 'Local development server',
    },
    {
      url: 'https://api.works-works-lite.dev.skewbdigital.co.uk',
      description: 'Dev API server',
    },
    {
      url: 'https://api.works-works-lite.dev.skewbdigital.co.uk',
      description: 'Production API server',
    },
  ],
  // Merge paths from all domains with their prefixes
  paths: {
    ...prefixPaths(jobsOpenApiSpec.paths, '/jobs'),
    ...prefixPaths(analyticsOpenApiSpec.paths, '/analytics'),
    ...prefixPaths(usersOpenApiSpec.paths, '/works-lite/users'),
    ...prefixPaths(teamsOpenApiSpec.paths, '/works-lite/teams'),
    ...prefixPaths(plannerOpenApiSpec.paths, '/works-lite/planner'),
    ...prefixPaths(settingsOpenApiSpec.paths, '/works-lite/settings'),
    ...prefixPaths(workOrderDirectionalOpenApiSpec.paths, '/works-lite/work-orders'),
  },
  // Security applied to all endpoints by default
  security: [{ bearerAuth: [] }],
  // Merge components (schemas, security schemes) from all domains
  components: {
    securitySchemes: {
      bearerAuth: {
        type: 'http',
        scheme: 'bearer',
        bearerFormat: 'JWT',
        description:
          'AWS Cognito JWT access token. Authenticate via POST /auth with email and password to receive tokens as HTTP-only cookies.',
      },
      cookieAuth: {
        type: 'apiKey',
        in: 'cookie',
        name: 'accessToken',
        description:
          'HTTP-only cookie set on successful authentication. Automatically included by the browser.',
      },
    },
    schemas: {
      ...jobsOpenApiSpec.components?.schemas,
      ...analyticsOpenApiSpec.components?.schemas,
      ...usersOpenApiSpec.components?.schemas,
      ...teamsOpenApiSpec.components?.schemas,
      ...plannerOpenApiSpec.components?.schemas,
      ...settingsOpenApiSpec.components?.schemas,
      ...workOrderDirectionalOpenApiSpec.components?.schemas,
    },
  },
  // Merge tags from all domains
  tags: [
    ...(analyticsOpenApiSpec.tags || []),
    ...(jobsOpenApiSpec.tags || []),
    ...(usersOpenApiSpec.tags || []),
    ...(teamsOpenApiSpec.tags || []),
    ...(plannerOpenApiSpec.tags || []),
    ...(settingsOpenApiSpec.tags || []),
  ],
};

/**
 * Export OpenAPI spec as JSON string for serving
 */
export const openApiSpecJson = JSON.stringify(openApiSpec, null, 2);
